import React from 'react';
import { Button, Input } from 'rsuite';
import { useNavigate } from 'react-router-dom';

import * as auth from '../../api/auth';
import { useAuth } from '../../common/AuthContext';

/**
 * Email icon SVG component
 */
const EmailIcon = () => (
  <svg
    className="login-field__icon"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 7l-10 6L2 7" />
  </svg>
);

/**
 * Lock icon SVG component
 */
const LockIcon = () => (
  <svg
    className="login-field__icon"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/**
 * Eye icon SVG component (password visible)
 */
const EyeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/**
 * Eye-off icon SVG component (password hidden)
 */
const EyeOffIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const ERROR_DISPLAY_DURATION = 8000; // 8 seconds

export default function Login() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');
  const [error, setError] = React.useState<string>('');
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [isShaking, setIsShaking] = React.useState<boolean>(false);

  const isFormValid = email.trim() !== '' && password.trim() !== '';

  // Auto-hide error after specified duration
  React.useEffect(() => {
    if (!error) {
      return;
    }

    const timer = setTimeout(() => {
      setError('');
    }, ERROR_DISPLAY_DURATION);

    return () => clearTimeout(timer);
  }, [error]);

  const triggerShake = () => {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
  };

  const login = async (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
    }
    setError('');
    try {
      await auth.auth(email, password);
      // Refresh AuthContext so it observes the new session cookie + populates
      // user email. After refresh resolves to 'authed', GuestRoute's
      // <Navigate> will also fire; we navigate explicitly here so the URL
      // updates immediately without an intermediate render cycle.
      const status = await refresh();
      if (status === 'authed') {
        navigate('/dashboard', { replace: true });
      } else {
        setError('Невірні дані для входу');
        triggerShake();
      }
    } catch (err: unknown) {
      setError('Невірні дані для входу');
      triggerShake();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  return (
    <div className="login-page">
      {/* Skip link for keyboard navigation */}
      <a href="#login-form" className="skip-link">
        Перейти до форми входу
      </a>

      {/* Screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {error && error}
      </div>

      <div className="video-container" aria-hidden="true">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="bgd-video"
          aria-hidden="true"
        >
          <source src="/video/bgd_video.mp4" type="video/mp4" />
        </video>
        <div className="video-gradient" aria-hidden="true"></div>
        <div className="video-overlay" aria-hidden="true"></div>
      </div>

      <div className={`login-card ${isShaking ? 'login-card--shake' : ''}`}>
        <div className="login-card__heading">
          <img
            src="/image/logo/main_white_logo.png"
            alt="Kryla"
            className="login-logo"
          />
          <h1 className="login-title">Admin Panel</h1>
          <p className="login-subtitle">Увійдіть до панелі керування</p>
        </div>

        <form className="login-form" id="login-form" onSubmit={login}>
          <div className="login-field login-field--with-icon">
            <label htmlFor="email-input">Email</label>
            <div className="login-input-wrapper">
              <EmailIcon />
              <Input
                placeholder="email@example.com"
                id="email-input"
                type="email"
                value={email}
                autoComplete="email"
                autoFocus
                onChange={value => {
                  setEmail(value);
                }}
              />
            </div>
          </div>

          <div className="login-field login-field--with-icon">
            <label htmlFor="password-input">Пароль</label>
            <div className="login-input-wrapper">
              <LockIcon />
              <Input
                placeholder="••••••••"
                id="password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                autoComplete="current-password"
                onChange={value => {
                  setPassword(value);
                }}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                aria-label={
                  showPassword ? 'Приховати пароль' : 'Показати пароль'
                }
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}

          <div className="login-actions">
            <Button
              appearance="primary"
              className="login-button"
              type="submit"
              disabled={!isFormValid}
            >
              Увійти
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
